/**
 * Standardizes a raw Flash Point value extracted from an SDS document into °C.
 * Behavior (per spec):
 * 1. Output is one of:
 *    - a single numeric value                          e.g. "61.5"
 *    - a range                                          e.g. "32–96"
 *    - a qualified value when a comparison operator      e.g. ">93"
 *      is present in the source (>, <, >=, <=)
 * 2. °F values are converted: °C = (°F - 32) × 5/9, rounded to 1 decimal.
 *    Whole-number results are shown without a trailing ".0".
 * 3. Testing-method annotations (ASTM/ISO/DIN codes, "open cup"/"closed cup",
 *    "c.c."/"o.c.", parenthetical method notes) are stripped.
 * 4. When the same value is given in both °C and °F, only the °C reading
 *    is kept.
 * 5. Ranges are preserved and normalized to `a–b` (en dash, no spaces).
 * 6. Non-applicable / non-numeric responses ("Not Applicable", "Nonflammable",
 *    "No data available", "N/A", etc.) return "".
 */

const NON_APPLICABLE_PATTERNS: RegExp[] = [
  /not\s*applicable/i,
  /not\s*determined/i,
  /no\s*data\s*(available)?/i,
  /non[\s-]?flammable/i,
  /not\s*flammable/i,
  /does\s*not\s*sustain\s*combustion/i,
  /non[\s-]?combustible/i,
  /not\s*relevant/i,
  /not\s*known/i,
  /\bn\/?a\b/i,
];
 
/** Strips test-method references and other non-value noise from the raw text. */
function stripMethodNoise(text: string): string {
  return text
    // Parenthetical method refs that don't themselves carry a unit reading,
    // e.g. "(ASTM D56)", "(c.c.)" — but keep parens that hold an alternate
    // numeric reading (e.g. "(>199.4°F)") so the duplicate-unit logic below
    // can still see and discard it.
    .replace(/\(([^)]*)\)/g, (whole, inner: string) =>
      /\d/.test(inner) ? whole.replace(/[()]/g, " ") : " ",
    )
    .replace(/\b(open|closed)\s*cup\b/gi, " ")
    .replace(/\bc\.?\s*c\.?\b/gi, " ")
    .replace(/\bo\.?\s*c\.?\b/gi, " ")
    .replace(/\bASTM\s*[A-Z]?\s*\d+\b/gi, " ")
    .replace(/\bISO\s*\d+\b/gi, " ")
    .replace(/\bDIN\s*\d+\b/gi, " ")
    .replace(/\bIP\s*\d+\b/gi, " ")
    .replace(/[:;,/]/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();
}
 
/** Converts a value to °C given its source unit ("C" | "F" | undefined→assume C). */
function toCelsius(value: number, unit?: string): number {
  return unit?.toUpperCase() === "F" ? ((value - 32) * 5) / 9 : value;
}
 
/** Formats a numeric °C value, rounding to 1 decimal and trimming a trailing ".0". */
function formatCelsius(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}
 
const RANGE_REGEX =
  /(-?\d+(?:\.\d+)?)\s*°?\s*([CF])?\s*(?:[-–—]|\bto\b)\s*(-?\d+(?:\.\d+)?)\s*°?\s*([CF])?/i;
 
const VALUE_REGEX = /(<=|>=|<|>)?\s*(-?\d+(?:\.\d+)?)\s*°?\s*([CF])?/gi;
 
export function sanitizeFlashPoint(
  rawFlashPoint: string | number | null | undefined,
): string {
  if (rawFlashPoint === null || rawFlashPoint === undefined) return "";
 
  const original = String(rawFlashPoint).trim();
  if (!original) return "";
 
  if (NON_APPLICABLE_PATTERNS.some((pattern) => pattern.test(original))) {
    return "";
  }
 
  if (!/\d/.test(original)) return "";
 
  const cleaned = stripMethodNoise(original);
  if (!cleaned || !/\d/.test(cleaned)) return "";
 
  // --- Range check (e.g. "32 °C – 96 °C", "58–76 °C", "12-13 °C") ---
  const rangeMatch = cleaned.match(RANGE_REGEX);
  if (rangeMatch) {
    const [, num1, unit1, num2, unit2] = rangeMatch;
    // A shared trailing unit (e.g. "12-13 °C") applies to both sides when
    // the first side has no unit of its own.
    const resolvedUnit1 = unit1 ?? unit2;
    const resolvedUnit2 = unit2 ?? unit1;
 
    const c1 = toCelsius(parseFloat(num1), resolvedUnit1);
    const c2 = toCelsius(parseFloat(num2), resolvedUnit2);
 
    if (!isNaN(c1) && !isNaN(c2)) {
      return `${formatCelsius(c1)}–${formatCelsius(c2)}`;
    }
  }
 
  // --- Single / qualified value, with duplicate-unit resolution ---
  // Collect every number+unit occurrence left in the cleaned text so that
  // when both °C and °F readings of the same value are present (or an OCR
  // duplicate exists), the °C reading always wins (Rule 4).
  const matches = Array.from(cleaned.matchAll(VALUE_REGEX)).filter(
    (m) => m[2] !== undefined && m[2] !== "" && m[2] !== "-",
  );
 
  if (matches.length === 0) return "";
 
  const celsiusMatch = matches.find((m) => m[3]?.toUpperCase() === "C");
  const chosen = celsiusMatch ?? matches[0];
 
  const [, qualifier, valueStr, unit] = chosen;
  const numericValue = parseFloat(valueStr);
  if (isNaN(numericValue)) return "";
 
  const celsiusValue = toCelsius(numericValue, unit);
  const normalizedQualifier = qualifier ? qualifier.trim() : "";
 
  return `${normalizedQualifier}${formatCelsius(celsiusValue)}`;
}


/**
 * Sanitizes and normalizes particle size distribution raw text strings
 * extracted from Safety Data Sheets (SDS).
 *
 * Output shapes (in priority order of detection):
 *  - Percentiles:      "D10: 5 µm, D50: 25 µm, D90: 100 µm"
 *  - Mesh size:        "200 mesh"
 *  - Range:            "10-100 µm"
 *  - Upper bound only:  "<75 µm"
 *  - Lower bound only:  ">100 µm"
 *  - Approximate:       "~50 µm"
 *  - Single value:      "50 µm"
 *  - Multiple of any of the above, comma-separated
 *  - null when nothing recognizable / descriptive-only / explicitly "not available"
 */
export function sanitizeParticleSizeDistribution(input: unknown): string | null {
  if (input === null || input === undefined) return null;

  const text = String(input).trim().replace(/\s+/g, ' ');
  if (!text) return null;

  // Explicit "no data" phrasing (English + Hungarian, extend as needed)
  if (/not\s*available|n\/?a\b|no\s*(particle|data)|not\s*(determined|applicable)|nincs\s*adat/i.test(text)) {
    return null;
  }

  // If there isn't a single digit in the string, there is nothing to extract:
  // this also covers purely descriptive terms ("Fine powder", "Coarse", "Dust", ...)
  if (!/\d/.test(text)) return null;

  const UNIT = '(?:mm|cm|nm|µm|μm|um|m)';

  const TO_UM: Record<string, number> = {
    nm: 0.001,
    'µm': 1,
    mm: 1000,
    cm: 10000,
    m: 1_000_000,
  };

  const normalizeUnit = (u?: string): string | undefined => {
    if (!u) return undefined;
    const low = u.toLowerCase();
    if (low === 'um' || low === 'µm' || low === 'μm') return 'µm';
    return low; // mm, cm, nm, m
  };

  const formatNum = (n: string | number): string => {
    const num = typeof n === 'string' ? parseFloat(n) : n;
    if (Number.isNaN(num)) return String(n);
    const rounded = Math.round(num * 10000) / 10000;
    return String(rounded);
  };

  const dedupe = (arr: string[]): string[] => Array.from(new Set(arr));

  // Builds a normalized "start-end unit" range, converting units if the two
  // ends were given in different units (e.g. "2mm to 8000 µm").
  const buildRange = (
    startVal: string,
    startUnit: string | undefined,
    endVal: string,
    endUnit: string | undefined
  ): string | null => {
    let sUnit = normalizeUnit(startUnit);
    let eUnit = normalizeUnit(endUnit);
    if (!sUnit && !eUnit) return null;
    if (!sUnit) sUnit = eUnit;
    if (!eUnit) eUnit = sUnit;

    if (sUnit === eUnit) {
      return `${formatNum(startVal)}-${formatNum(endVal)} ${eUnit}`;
    }

    const sFactor = TO_UM[sUnit as string];
    const eFactor = TO_UM[eUnit as string];
    if (sFactor === undefined || eFactor === undefined) {
      return `${formatNum(startVal)} ${sUnit}-${formatNum(endVal)} ${eUnit}`;
    }

    // Convert both ends to whichever unit is more precise (smaller scale)
    const targetUnit = sFactor <= eFactor ? sUnit : eUnit;
    const targetFactor = TO_UM[targetUnit as string];
    const startConverted = (parseFloat(startVal) * sFactor) / targetFactor;
    const endConverted = (parseFloat(endVal) * eFactor) / targetFactor;
    return `${formatNum(startConverted)}-${formatNum(endConverted)} ${targetUnit}`;
  };

  // ---------------------------------------------------------------------
  // 1. Distribution percentiles: D10 = 5 µm / D50: 25 µm / (D50): 25 µm
  // ---------------------------------------------------------------------
  {
    const percentileRegex = new RegExp(`D\\s*(\\d{1,3})\\s*\\)?\\s*[:=]\\s*([\\d.]+)\\s*${UNIT}`, 'gi');
    const found: { d: number; text: string }[] = [];
    let m: RegExpExecArray | null;
    while ((m = percentileRegex.exec(text)) !== null) {
      const unit = normalizeUnit(m[3]) ?? m[0].match(new RegExp(UNIT, 'i'))?.[0];
      found.push({ d: parseInt(m[1], 10), text: `D${m[1]}: ${formatNum(m[2])} ${unit}` });
    }
    if (found.length > 0) {
      found.sort((a, b) => a.d - b.d);
      return dedupe(found.map((f) => f.text)).join(', ');
    }
  }

  // ---------------------------------------------------------------------
  // 2. Percent-passing with an explicit threshold: "90% < 75 µm", "95% below 100 µm"
  // ---------------------------------------------------------------------
  {
    const percentThresholdRegex = new RegExp(
      `\\d{1,3}\\s*%\\s*(?:passing)?\\s*(?:is\\s*)?(less than|below|under|<)\\s*([\\d.]+)\\s*(${UNIT.slice(3, -1)})`,
      'gi'
    );
    const results: string[] = [];
    let m: RegExpExecArray | null;
    while ((m = percentThresholdRegex.exec(text)) !== null) {
      results.push(`<${formatNum(m[2])} ${normalizeUnit(m[3])}`);
    }
    if (results.length > 0) return dedupe(results).join(', ');
  }

  // ---------------------------------------------------------------------
  // 3. Mesh sizes: "200 mesh", "80% passing 200 mesh"
  // ---------------------------------------------------------------------
  {
    const meshRegex = /(\d{1,4})\s*mesh\b/gi;
    const results: string[] = [];
    let m: RegExpExecArray | null;
    while ((m = meshRegex.exec(text)) !== null) {
      results.push(`${m[1]} mesh`);
    }
    if (results.length > 0) return dedupe(results).join(', ');
  }

  // ---------------------------------------------------------------------
  // 4. Explicitly labeled Min/Max pairs (either order)
  //    "Min 2mm Max 8mm", "Minimum: 10 µm Maximum: 100 µm",
  //    "min. 5 µm, max. 50 µm"
  // ---------------------------------------------------------------------
  {
    const minMaxRegex = new RegExp(
      `min(?:imum)?\\.?\\s*:?\\s*([\\d.]+)\\s*(${UNIT.slice(3, -1)})?\\s*,?\\s*max(?:imum)?\\.?\\s*:?\\s*([\\d.]+)\\s*(${UNIT.slice(3, -1)})`,
      'gi'
    );
    const maxMinRegex = new RegExp(
      `max(?:imum)?\\.?\\s*:?\\s*([\\d.]+)\\s*(${UNIT.slice(3, -1)})?\\s*,?\\s*min(?:imum)?\\.?\\s*:?\\s*([\\d.]+)\\s*(${UNIT.slice(3, -1)})`,
      'gi'
    );

    const results: string[] = [];
    let m: RegExpExecArray | null;
    while ((m = minMaxRegex.exec(text)) !== null) {
      const r = buildRange(m[1], m[2] ?? m[4], m[3], m[4]);
      if (r) results.push(r);
    }
    if (results.length === 0) {
      while ((m = maxMinRegex.exec(text)) !== null) {
        // group1/2 = max val/unit, group3/4 = min val/unit -> output as min-max
        const r = buildRange(m[3], m[4] ?? m[2], m[1], m[2]);
        if (r) results.push(r);
      }
    }
    if (results.length > 0) return dedupe(results).join(', ');

    // Only one bound labeled (min alone, or max alone)
    const minOnly = new RegExp(`min(?:imum)?\\.?\\s*:?\\s*([\\d.]+)\\s*(${UNIT.slice(3, -1)})`, 'i').exec(text);
    const maxOnly = new RegExp(`max(?:imum)?\\.?\\s*:?\\s*([\\d.]+)\\s*(${UNIT.slice(3, -1)})`, 'i').exec(text);
    if (minOnly && !maxOnly) return `>${formatNum(minOnly[1])} ${normalizeUnit(minOnly[2])}`;
    if (maxOnly && !minOnly) return `<${formatNum(maxOnly[1])} ${normalizeUnit(maxOnly[2])}`;
  }

  // ---------------------------------------------------------------------
  // 5. Generic numeric ranges: "10-100 µm", "2 mm to 8 mm", possibly several
  //    ("10-50 µm and 100-200 µm", "Fine: 10-50 µm, Coarse: 100-500 µm")
  // ---------------------------------------------------------------------
  {
    const rangeRegex = new RegExp(
      `([\\d.]+)\\s*(${UNIT.slice(3, -1)})?\\s*(?:-|–|—|to)\\s*([\\d.]+)\\s*(${UNIT.slice(3, -1)})`,
      'gi'
    );
    const results: string[] = [];
    let m: RegExpExecArray | null;
    while ((m = rangeRegex.exec(text)) !== null) {
      const r = buildRange(m[1], m[2], m[3], m[4]);
      if (r) results.push(r);
    }
    if (results.length > 0) return dedupe(results).join(', ');
  }

  // ---------------------------------------------------------------------
  // 6. Comparative phrases: less than / below / under / <  ,  greater than / above / over / >
  // ---------------------------------------------------------------------
  {
    const comparativeRegex = new RegExp(
      `(less than|below|under|greater than|above|over|more than|<=|>=|<|>)\\s*([\\d.]+)\\s*(${UNIT.slice(3, -1)})`,
      'gi'
    );
    const mapOperator = (op: string): string => {
      const o = op.toLowerCase();
      if (o === '<=') return '<=';
      if (o === '>=') return '>=';
      if (['less than', 'below', 'under', '<'].includes(o)) return '<';
      if (['greater than', 'above', 'over', 'more than', '>'].includes(o)) return '>';
      return o;
    };
    const results: string[] = [];
    let m: RegExpExecArray | null;
    while ((m = comparativeRegex.exec(text)) !== null) {
      results.push(`${mapOperator(m[1])}${formatNum(m[2])} ${normalizeUnit(m[3])}`);
    }
    if (results.length > 0) return dedupe(results).join(', ');
  }

  // ---------------------------------------------------------------------
  // 7. Approximate values: "~50 µm", "Approx. 50 µm", "Around 50 µm"
  // ---------------------------------------------------------------------
  {
    const approxRegex = new RegExp(`(?:approx(?:imately)?\\.?|~|around)\\s*([\\d.]+)\\s*(${UNIT.slice(3, -1)})`, 'gi');
    const results: string[] = [];
    let m: RegExpExecArray | null;
    while ((m = approxRegex.exec(text)) !== null) {
      results.push(`~${formatNum(m[1])} ${normalizeUnit(m[2])}`);
    }
    if (results.length > 0) return dedupe(results).join(', ');
  }

  // ---------------------------------------------------------------------
  // 8. Bare single value(s): "50 µm", "2 mm"
  // ---------------------------------------------------------------------
  {
    const singleRegex = new RegExp(`([\\d.]+)\\s*(${UNIT.slice(3, -1)})\\b`, 'gi');
    const results: string[] = [];
    let m: RegExpExecArray | null;
    while ((m = singleRegex.exec(text)) !== null) {
      results.push(`${formatNum(m[1])} ${normalizeUnit(m[2])}`);
    }
    if (results.length > 0) return dedupe(results).join(', ');
  }

  return null;
}
